<template>
  <div>
    <div class="pentagon-background tw-bg-white tw-px-4 tw-py-8">
      <div test_id="video-section">
        <div class="tw-mx-auto tw-max-w-5xl">
          <BaseButton
            color="outline-gray"
            href="/content-library/videos"
            is-inertia-link
          >
            <ArrowLeftIcon class="tw-mr-2 tw-h-4 tw-w-4" />
            Back to video library
          </BaseButton>
          <h1 class="tw-heading-1 tw-mt-6 tw-text-4xl">{{ video.title }}</h1>
          <div class="tw-mt-6 tw-inline-flex tw-items-center tw-space-x-2">
            <span class="tw-text-sm">
              Posted on {{ video.formattedCreatedAt }}
            </span>
            <div>
              <Tag v-for="tag in video.tags" :key="tag.id" class="tw-m-0.5">
                {{ tag.name }}
              </Tag>
            </div>
          </div>
          <hr class="tw-my-4" />
          <p class="tw-text-xl">{{ video.subtitle }}</p>
        </div>
      </div>
      <div class="tw-mt-4" test_id="video-author-section">
        <div class="tw-mx-auto tw-max-w-5xl tw-space-y-8">
          <iframe
            v-if="video.wistiaId"
            :src="`https://fast.wistia.com/embed/iframe/${video.wistiaId}`"
            allowfullscreen
            class="tw-aspect-video tw-w-full tw-rounded-xl tw-shadow-xxl tw-shadow-neutral-400"
          />
          <iframe
            v-else-if="video.youtubeId"
            class="tw-aspect-video tw-w-full tw-rounded-xl tw-shadow-xxl tw-shadow-neutral-400"
            :src="`https://www.youtube.com/embed/${video.youtubeId}`"
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          />
          <!--Video Library is unlocked for everyone starting from 21 of November 2023-->
          <!--<UnlockVideoCard v-if="hasRole(ROLE.GUEST)" />-->
          <div>
            <h2 class="tw-heading-2">About this video</h2>
            <p class="tw-mt-4 tw-whitespace-pre-line tw-text-neutral-600">
              {{ video.about }}
            </p>
          </div>
          <div>
            <h2 class="tw-heading-2">In this video you will learn:</h2>
            <p class="tw-mt-4 tw-whitespace-pre-line tw-text-neutral-600">
              {{ video.learnDescription }}
            </p>
          </div>
          <Mentor
            :mentor="video.author"
            :mentor-added-to-lists="video.author.addedToLists"
            class="tw-rounded-xl tw-border tw-border-neutral-200 tw-shadow-xl"
          />
          <template v-if="video.author.reviews.length">
            <div
              class="tw-text-xs tw-font-semibold tw-uppercase tw-text-neutral-500"
            >
              What others say about {{ video.author.firstName }}?
            </div>
            <Review
              v-for="review in video.author.reviews"
              :key="review.id"
              :review="review"
            />
          </template>
        </div>
      </div>
    </div>
    <div
      class="tw-mx-auto tw-mt-12 tw-max-w-screen-2xl tw-px-4"
      test_id="suggested-videos"
    >
      <div class="tw-text-center tw-text-xs tw-font-semibold tw-uppercase">
        You may also like...
      </div>
      <div
        class="tw-mt-6 tw-grid tw-gap-4 lg:tw-grid-cols-2 xl:tw-grid-cols-3 xl:tw-gap-6"
      >
        <ContentLibraryVideoCard
          v-for="(suggestedVideo, index) in suggestedVideos"
          :key="suggestedVideo.id"
          :video="suggestedVideo"
          :class="{ 'lg:tw-col-span-2 xl:tw-col-span-1': index === 2 }"
          test_id="video-card"
        />
      </div>
    </div>

    <UnlockFullAccessModal v-if="hasRole(ROLE.GUEST)" />
  </div>
</template>

<script setup>
import BaseButton from '@/shared/components/BaseButton.vue'
import ArrowLeftIcon from '@/assets/images/icons/arrow-left.svg'
import Tag from '@/shared/components/Tag.vue'
import ContentLibraryVideoCard from '@/modules/growthmentor/content-library/components/ContentLibraryVideoCard.vue'
import Mentor from '@/modules/growthmentor/mentors/components/Mentor.vue'
import Review from '@/modules/growthmentor/mentors/components/reviews/Review.vue'
import UnlockFullAccessModal from '@/shared/components/modals/UnlockFullAccessModal.vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { ROLE } from '@/shared/data/permissionSystem.js'

defineProps({
  video: {
    type: Object,
    required: true,
  },
  suggestedVideos: {
    type: Array,
    required: true,
  },
})

const { hasRole } = usePermissionSystem()
</script>
